import type { ApiError, Page } from '@simpleacs/shared';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiError | null, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.error?.code ?? 'internal_error';
    this.details = body?.error?.details;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const res = await fetch(withQuery(`/api${path}`, query), {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();

  /**
   * Not everything on this path speaks JSON.
   *
   * The API always does; nginx does not. A gateway timeout on a slow chassis
   * read, or a restart mid-deploy, returns an HTML error page — and parsing it
   * threw `Unexpected token '<'`, which tells the operator nothing about a
   * request that timed out or a server that was restarting.
   */
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    if (res.ok) {
      throw new ApiRequestError(res.status, null, 'The server sent something this app cannot read.');
    }
    throw new ApiRequestError(
      res.status,
      null,
      res.status === 502 || res.status === 503
        ? 'The API is not answering — it may be restarting. Try again in a moment.'
        : res.status === 504
          ? 'The OLT took too long to answer and the request timed out. It may still have run — re-read before retrying.'
          : res.statusText || 'The server returned an error page.',
    );
  }

  if (!res.ok) {
    throw new ApiRequestError(res.status, parsed as ApiError | null, res.statusText);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  // A body on DELETE is unusual but correct where the request names several
  // things to remove; the alternative is a URL that cannot hold them.
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

export type { Page };
